import {useNavigate} from "react-router-dom";
import React, {useEffect, useRef} from "react";
import SockJsClient from 'react-stomp';
import {getVenueSession} from "../../requests/AppSession";
import {baseUrl, vtDancerMessageURI} from "../../requests/NetworkRequestAuth0";
import CircularProgress from "@mui/material/CircularProgress";
import {
    ArrowDownIcon,
    ArrowPathIcon,
    ArrowUpIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ForwardIcon,
    PlusIcon,
    UsersIcon,
    XMarkIcon as XMarkMiniIcon
} from "@heroicons/react/20/solid";
import {useAuth0} from "@auth0/auth0-react";
import {findVenueDancers} from "../requests/AppRequestDancer";
import {endTipping, findVenueStageQueue, switchGirl, updateVenueStageQueue} from "../requests/AppRequestMessage";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from '@headlessui/react'
import {XMarkIcon} from '@heroicons/react/24/outline'
import {DancerListSkeleton} from "./VTSkeletons";

export default function VTDancerSelection() {

    let navigate = useNavigate();

    const {isLoading, getAccessTokenSilently} = useAuth0();

    const [open, setOpen] = React.useState(false);

    const [venueId, setVenueId] = React.useState(null);

    const [people, setPeople] = React.useState(null);
    const [selectedDancerId, setSelectedDancerId] = React.useState(null);

    // Ordered list of dancer ids the DJ intends to put on stage next (persisted server-side).
    const [queue, setQueue] = React.useState([]);

    // When true (default), a dancer whose set ends rotates to the back of the queue instead of
    // being removed, so the lineup loops. Persisted per-venue on the server.
    const [rotation, setRotation] = React.useState(true);

    // Collapsible sections so a long Rotation Queue / All Dancers list can be folded away. Both
    // default open.
    const [queueOpen, setQueueOpen] = React.useState(true);
    const [rosterOpen, setRosterOpen] = React.useState(true);

    const [searchText, setSearchText] = React.useState("");

    const [errorMessage, setErrorMessage] = React.useState(null);

    const [isSwitchingGirls, setIsSwitchingGirls] = React.useState(false);

    const [isRefreshing, setIsRefreshing] = React.useState(false);
    const [refreshSuccess, setRefreshSuccess] = React.useState(false);

    useEffect(() => {
        getVenueSession().then(venueSession => {
            if (venueSession && venueSession.id !== "null") {
                setVenueId(venueSession.venueId);
            } else {
                navigate('/login/mobile', {replace: true});
            }

            if (!venueSession.stageTipFeatureFlag) {
                navigate('/login/mobile', {replace: true});
            }
        })
        return function cleanup() {

        };
    }, []);

    const getSuccess = async responseJson => {
        setIsRefreshing(false);
        if (
            responseJson
        ) {
            setPeople(responseJson.dancers);
            setSelectedDancerId(responseJson.dancerIdMainStage);
        } else {
            setErrorMessage("Error getting dancer list, please refresh and verify Internet Connection.");
        }
    };

    const getSuccessAndShowRefresh = async responseJson => {
        setIsRefreshing(false);
        if (
            responseJson
        ) {
            setPeople(responseJson.dancers);
            setSelectedDancerId(responseJson.dancerIdMainStage);
            showSuccessfullyRefreshed();
        } else {
            setErrorMessage("Error getting dancer list, please refresh and verify Internet Connection.");
        }
    };

    const showSuccessfullyRefreshed = () => {
        setRefreshSuccess(true);
        setTimeout(() => {
            setRefreshSuccess(false);
        }, 2000);
    };

    const getError = forceLogout => {
        setIsRefreshing(false);
        setErrorMessage("Error getting dancer list, please refresh and verify Internet Connection.");
    };

    // ---- Stage queue ----------------------------------------------------

    const queueSuccess = async responseJson => {
        if (responseJson && Array.isArray(responseJson.stageQueue)) {
            setQueue(responseJson.stageQueue);
        }
        if (responseJson && typeof responseJson.rotation === "boolean") {
            setRotation(responseJson.rotation);
        }
    };

    const queueError = forceLogout => {
        // Non-fatal: the DJ can still operate the roster without the queue.
    };

    const loadQueue = () => {
        if (!isLoading && venueId !== null) {
            findVenueStageQueue(venueId, queueSuccess, queueError, getAccessTokenSilently);
        }
    };

    // Pending debounced reorder save, so a rapid up/down shuffle sends ONE PUT (and one broadcast)
    // instead of one per tap.
    const persistTimer = useRef(null);

    // Persist a new queue order (and rotation setting) to the server immediately. Optimistic: the
    // local state is already set by the caller; the server response/broadcast reconciles it.
    const persistQueue = (newQueue, newRotation = rotation) => {
        if (!isLoading && venueId !== null) {
            updateVenueStageQueue(venueId, newQueue, newRotation, queueSuccess, queueError, getAccessTokenSilently);
        }
    };

    // Cancel any pending debounced reorder save. Called right before an immediate add/remove/toggle,
    // which sends the full latest queue array and therefore supersedes the pending reorder.
    const cancelPendingPersist = () => {
        if (persistTimer.current) {
            clearTimeout(persistTimer.current);
            persistTimer.current = null;
        }
    };

    // Debounced persist for rapid reorders: coalesce bursts of up/down taps into a single save.
    const persistQueueDebounced = (newQueue) => {
        if (persistTimer.current) {
            clearTimeout(persistTimer.current);
        }
        persistTimer.current = setTimeout(() => {
            persistTimer.current = null;
            persistQueue(newQueue);
        }, 500);
    };

    // Clear any pending timer on unmount so it doesn't fire against a torn-down component.
    useEffect(() => {
        return () => {
            if (persistTimer.current) {
                clearTimeout(persistTimer.current);
                persistTimer.current = null;
            }
        };
    }, []);

    const toggleRotation = () => {
        const next = !rotation;
        setRotation(next);
        cancelPendingPersist();
        persistQueue(queue, next);
    };

    const addToQueue = (dancerId) => {
        if (queue.includes(dancerId) || selectedDancerId === dancerId) {
            return;
        }
        const newQueue = [...queue, dancerId];
        setQueue(newQueue);
        cancelPendingPersist();
        persistQueue(newQueue);
    };

    const removeFromQueue = (dancerId) => {
        const newQueue = queue.filter((id) => id !== dancerId);
        setQueue(newQueue);
        cancelPendingPersist();
        persistQueue(newQueue);
    };

    const moveInQueue = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= queue.length) {
            return;
        }
        const newQueue = [...queue];
        const temp = newQueue[index];
        newQueue[index] = newQueue[target];
        newQueue[target] = temp;
        setQueue(newQueue);
        // Debounced: a DJ often taps up/down several times in a row.
        persistQueueDebounced(newQueue);
    };

    // ---- Stage control --------------------------------------------------

    const switchGirlSuccess = async responseJson => {
        setIsSwitchingGirls(false);
        if (
            responseJson &&
            responseJson.id &&
            responseJson.name
        ) {
            setSelectedDancerId(responseJson.id);
            // Optimistic: the dancer that just went on stage is no longer "up next". The server
            // reconciles the authoritative queue (in rotation mode it also appends the dancer whose
            // set just ended to the back) and broadcasts QUEUE_CHANGED, which this device also
            // receives — so no explicit refetch is needed here.
            setQueue((q) => q.filter((id) => id !== responseJson.id));
        } else {
            setOpen(true);
            setErrorMessage("Error selecting dancer, please verify Internet Connection and try again.");
        }
    };

    const switchGirlError = forceLogout => {
        setIsSwitchingGirls(false);
        setOpen(true);
        if (!isLoading && venueId !== null) {
            findVenueDancers(venueId, getSuccess, getError, getAccessTokenSilently);
        }
        setErrorMessage("Error selecting dancer, please verify Internet Connection and try again.");
    };

    const setOnStage = (dancerId) => {
        setIsSwitchingGirls(true);
        setErrorMessage(null);
        switchGirl(venueId, dancerId, switchGirlSuccess, switchGirlError, getAccessTokenSilently);
    };

    // Big "Next" action: put the top-of-queue dancer on stage.
    const playNext = () => {
        if (queue.length === 0 || isSwitchingGirls) {
            return;
        }
        setOnStage(queue[0]);
    };

    const endTippingSuccess = async responseJson => {
        setIsSwitchingGirls(false);
        if (
            responseJson &&
            responseJson.message
        ) {
            setSelectedDancerId(null);
            // In rotation mode the server moves the just-ended dancer to the back of the queue and
            // broadcasts QUEUE_CHANGED, which this device also receives — no explicit refetch here.
        } else {
            setErrorMessage("Error ending tipping, please verify Internet Connection and try again.");
        }
    };

    const endTippingError = forceLogout => {
        setIsSwitchingGirls(false);
        setErrorMessage("Error ending tipping, please verify Internet Connection and try again.");
    };

    const clearStage = () => {
        setIsSwitchingGirls(true);
        endTipping(venueId, endTippingSuccess, endTippingError, getAccessTokenSilently);
    };

    // ---- Load + live sync ----------------------------------------------

    useEffect(() => {
        if (!isLoading && venueId !== null) {
            findVenueDancers(venueId, getSuccess, getError, getAccessTokenSilently);
            loadQueue();
        }
        return function cleanup() {

        };
    }, [isLoading, venueId]);

    useEffect(() => {
        if (!isLoading && venueId !== null) {
            // Fallback poll (the websocket is the primary live-update path).
            const intervalId = setInterval(() => {
                findVenueDancers(venueId, getSuccess, getError, getAccessTokenSilently);
                loadQueue();
            }, 60000); // Every minute

            return () => {
                clearInterval(intervalId);
            };
        }
    }, [isLoading, venueId]);

    // React to venue websocket messages so the DJ screen stays in sync across devices and with
    // dancer check-ins, without waiting on the poll.
    const onMessageReceive = (message) => {
        if (!message || !message.messageCode) {
            return;
        }
        switch (message.messageCode) {
            case "QUEUE_CHANGED":
                if (Array.isArray(message.stageQueue)) {
                    setQueue(message.stageQueue);
                }
                if (typeof message.rotation === "boolean") {
                    setRotation(message.rotation);
                }
                break;
            case "DANCER_LIST_CHANGED":
                // A dancer checked in; refresh the roster promptly.
                findVenueDancers(venueId, getSuccess, getError, getAccessTokenSilently);
                break;
            case "SWITCH_GIRL":
                if (message.dancer && message.dancer.id) {
                    setSelectedDancerId(message.dancer.id);
                    setQueue((q) => q.filter((id) => id !== message.dancer.id));
                }
                break;
            case "END_TIPPING":
                setSelectedDancerId(null);
                break;
            default:
                break;
        }
    };

    // ---- Derived view data ---------------------------------------------

    const onStageDancer = selectedDancerId ? (people || []).find((p) => p.id === selectedDancerId) : null;
    const onDeckId = queue.length > 0 ? queue[0] : null;
    const onDeckDancer = onDeckId ? (people || []).find((p) => p.id === onDeckId) : null;

    const applySearch = (value) => {
        setSearchText(value);
    };

    // Roster shown in the "All dancers" section: everyone, optionally name-filtered.
    const rosterFiltered = (people || []).filter((dancer) => {
        if (!searchText || searchText.length === 0) {
            return true;
        }
        return dancer.name && dancer.name.substring(0, searchText.length).toLowerCase() === searchText.toLowerCase();
    });

    const nameFor = (id) => {
        const d = (people || []).find((p) => p.id === id);
        return d ? d.name : "(dancer)";
    };

    return (
        <>
            <div>

                <div className="mx-auto max-w-7xl px-4 pt-10 pb-2 sm:px-6 lg:px-8">
                    <div className="sm:align-center sm:flex sm:flex-col">
                        <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-100 text-center sm:text-5xl">Dancer<span
                            className="text-pink-400">Selection</span></h1>
                    </div>
                </div>

                {/* ON STAGE + NEXT — sticky so the DJ's command center never scrolls away. z-index is
                    kept just under the header menu (Popover.Panel is z-10) so the menu opens ABOVE
                    this card, while still staying above the scrolling list content below. --------- */}
                <div className="sticky top-0 z-[5] bg-gray-50/95 pt-2 pb-3 backdrop-blur">
                    <div className="mx-auto max-w-2xl px-6">
                        <div className="rounded-xl bg-white dark:bg-gray-800 p-5 shadow-sm ring-1 ring-gray-200 dark:ring-gray-700">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">On Stage</p>
                            {onStageDancer ?
                                <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-gray-100">{onStageDancer.name}</p>
                                :
                                <p className="mt-1 text-2xl font-medium text-gray-400 dark:text-gray-500">No one on stage</p>
                            }

                            <div className="mt-3 flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Up Next</p>
                                    {onDeckDancer ?
                                        <p className="mt-0.5 text-lg font-semibold text-pink-600">{onDeckDancer.name}</p>
                                        :
                                        <p className="mt-0.5 text-sm font-medium text-gray-400 dark:text-gray-500">Queue is empty</p>
                                    }
                                </div>
                                <button
                                    type="button"
                                    disabled={isSwitchingGirls || queue.length === 0}
                                    onClick={playNext}
                                    className="inline-flex items-center gap-x-2 rounded-lg bg-pink-600 px-5 py-3 text-base font-bold text-white shadow-sm transition hover:bg-pink-500 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                                >
                                    Next
                                    {isSwitchingGirls ?
                                        <CircularProgress color="inherit" size={18}/> :
                                        <ForwardIcon aria-hidden="true" className="h-5 w-5"/>}
                                </button>
                            </div>

                            {selectedDancerId && <button
                                disabled={isSwitchingGirls}
                                className="mt-4 flex w-full items-center justify-center rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                                onClick={clearStage}
                            >
                                Clear Stage / End Tipping {isSwitchingGirls && <CircularProgress color="inherit" size={20}/>}
                            </button>}

                            {errorMessage && !open && <p className="mt-3 text-sm font-medium text-red-600">{errorMessage}</p>}
                        </div>
                    </div>
                </div>

                {/* Lists: side-by-side on md+, stacked on phones. Each section collapses. --------- */}
                <div className="mx-auto mt-4 max-w-5xl px-6 pb-12 md:grid md:grid-cols-2 md:items-start md:gap-8">

                    {/* QUEUE */}
                    <div className="mt-2 md:mt-0">
                        <button
                            type="button"
                            onClick={() => setQueueOpen((o) => !o)}
                            aria-expanded={queueOpen}
                            className="flex w-full items-center justify-between gap-2 py-1 text-left"
                        >
                            <span className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                <ChevronDownIcon
                                    className={"h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform " + (queueOpen ? "" : "-rotate-90")}
                                />
                                Rotation Queue
                                {queue.length > 0 &&
                                    <span className="rounded-full bg-pink-100 px-2 py-0.5 text-xs font-bold text-pink-700">
                                        {queue.length}
                                    </span>
                                }
                            </span>
                            <span
                                onClick={(e) => { e.stopPropagation(); toggleRotation(); }}
                                role="switch"
                                aria-checked={rotation}
                                className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200"
                            >
                                <span className={rotation ? "text-pink-600" : "text-gray-400 dark:text-gray-500"}>Rotation</span>
                                <span
                                    className={
                                        "relative inline-flex h-5 w-9 items-center rounded-full transition " +
                                        (rotation ? "bg-pink-600" : "bg-gray-300")
                                    }
                                >
                                    <span
                                        className={
                                            "inline-block h-4 w-4 transform rounded-full bg-white transition " +
                                            (rotation ? "translate-x-4" : "translate-x-1")
                                        }
                                    />
                                </span>
                            </span>
                        </button>
                        {queueOpen && <>
                        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                            {rotation
                                ? "On — a dancer who finishes her set goes to the back of the queue."
                                : "Off — a dancer who finishes her set is removed from the queue."}
                        </p>
                        {queue.length === 0 ?
                            <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">Add dancers from All Dancers to build the lineup.</p>
                            :
                            <ul className="mt-2 divide-y divide-gray-200 dark:divide-gray-700 rounded-xl bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700">
                                {queue.map((dancerId, index) => (
                                    <li key={dancerId} className="flex items-center justify-between px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-pink-100 text-xs font-bold text-pink-700">
                                                {index + 1}
                                            </span>
                                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{nameFor(dancerId)}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                disabled={index === 0}
                                                onClick={() => moveInQueue(index, -1)}
                                                className="rounded-md p-2 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300"
                                                aria-label="Move up"
                                            >
                                                <ArrowUpIcon className="h-5 w-5"/>
                                            </button>
                                            <button
                                                type="button"
                                                disabled={index === queue.length - 1}
                                                onClick={() => moveInQueue(index, 1)}
                                                className="rounded-md p-2 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300"
                                                aria-label="Move down"
                                            >
                                                <ArrowDownIcon className="h-5 w-5"/>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => removeFromQueue(dancerId)}
                                                className="rounded-md p-2 text-gray-400 dark:text-gray-500 hover:bg-red-50 hover:text-red-600"
                                                aria-label="Remove from queue"
                                            >
                                                <XMarkMiniIcon className="h-5 w-5"/>
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        }
                        </>}
                    </div>

                    {/* ALL DANCERS */}
                    <div className="mt-6 md:mt-0">
                        <button
                            type="button"
                            onClick={() => setRosterOpen((o) => !o)}
                            aria-expanded={rosterOpen}
                            className="flex w-full items-center gap-1.5 py-1 text-left text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                        >
                            <ChevronDownIcon
                                className={"h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform " + (rosterOpen ? "" : "-rotate-90")}
                            />
                            All Dancers
                            {people &&
                                <span className="rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-bold text-gray-600 dark:text-gray-400">
                                    {people.length}
                                </span>
                            }
                        </button>
                        {rosterOpen && <>
                        <div className="relative mt-2 rounded-md shadow-sm">
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                <UsersIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true"/>
                            </div>
                            <input
                                type="text"
                                name="search"
                                value={searchText}
                                className="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 dark:text-gray-100 dark:bg-gray-800 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-pink-600 sm:text-sm sm:leading-6"
                                placeholder="Search"
                                onChange={(event) => applySearch(event.target.value)}
                            />
                        </div>

                        <div className="mt-3 flex justify-center">
                            {refreshSuccess ?
                                <button
                                    type="button"
                                    disabled={true}
                                    className="inline-flex items-center gap-x-2 rounded-md bg-green-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-xs"
                                >
                                    Successfully Refreshed
                                    <CheckCircleIcon aria-hidden="true" className="-mr-0.5 size-5"/>
                                </button>
                                : <button
                                    onClick={() => {
                                        if (!isLoading && venueId !== null) {
                                            setIsRefreshing(true);
                                            findVenueDancers(venueId, getSuccessAndShowRefresh, getError, getAccessTokenSilently);
                                            loadQueue();
                                        }
                                    }}
                                    type="button"
                                    disabled={isRefreshing}
                                    className="inline-flex items-center gap-x-2 rounded-md bg-pink-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-pink-500"
                                >
                                    Refresh List
                                    {isRefreshing ?
                                        <ArrowPathIcon aria-hidden="true" className="-mr-0.5 size-5 animate-spin"/> :
                                        <ArrowPathIcon aria-hidden="true" className="-mr-0.5 size-5"/>}
                                </button>}
                        </div>

                        <div className="mt-4">
                            {!people && <div className="mx-6"><DancerListSkeleton/></div>}
                            {people && people.length === 0 &&
                                <h3 className="mx-6 text-sm text-gray-500 dark:text-gray-400">No Dancers are checked in.</h3>}
                            <ul className="space-y-2">
                                {rosterFiltered.map((person) => {
                                    const isOnStage = selectedDancerId === person.id;
                                    const inQueue = queue.includes(person.id);
                                    const initial = person.name ? person.name.trim().charAt(0).toUpperCase() : "?";
                                    return (
                                        <li
                                            key={person.id}
                                            className={
                                                "flex items-center gap-3 rounded-xl px-4 py-3 transition " +
                                                (isOnStage
                                                    ? "bg-pink-50 dark:bg-pink-950/40 ring-1 ring-pink-300 dark:ring-pink-700 border-l-4 border-pink-500 shadow-sm"
                                                    : inQueue
                                                        ? "bg-white dark:bg-gray-800 ring-1 ring-pink-200 dark:ring-pink-800 border-l-4 border-pink-200 dark:border-pink-800"
                                                        : "bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 border-l-4 border-transparent hover:ring-gray-300 dark:hover:ring-gray-600")
                                            }
                                        >
                                            {/* Monogram avatar */}
                                            <div
                                                className={
                                                    "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold " +
                                                    (isOnStage ? "bg-pink-500 text-white" : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300")
                                                }
                                            >
                                                {initial}
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <p className={"truncate font-semibold " + (isOnStage ? "text-pink-900 dark:text-pink-200" : "text-gray-900 dark:text-gray-100")}>
                                                    {person.name}
                                                </p>
                                                {isOnStage &&
                                                    <span className="mt-0.5 inline-flex items-center gap-1.5 rounded-full bg-pink-600 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
                                                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white"/>
                                                        On Stage
                                                    </span>
                                                }
                                            </div>

                                            {!isOnStage && <div className="flex flex-shrink-0 items-center gap-2">
                                                {inQueue ?
                                                    <button
                                                        type="button"
                                                        disabled={isSwitchingGirls}
                                                        title="Remove from queue"
                                                        aria-label={"Remove " + (person.name || "dancer") + " from queue"}
                                                        className="inline-flex items-center gap-1.5 rounded-full bg-pink-50 py-1.5 pl-3 pr-2 text-xs font-semibold text-pink-700 ring-1 ring-inset ring-pink-200 transition hover:bg-pink-100 active:bg-pink-200 disabled:cursor-not-allowed disabled:opacity-50"
                                                        onClick={() => removeFromQueue(person.id)}
                                                    >
                                                        In Queue
                                                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-pink-200 text-pink-800">
                                                            <XMarkMiniIcon className="h-3.5 w-3.5"/>
                                                        </span>
                                                    </button>
                                                    :
                                                    <button
                                                        disabled={isSwitchingGirls}
                                                        className="inline-flex items-center gap-1 rounded-full bg-white dark:bg-gray-800 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 transition hover:bg-gray-50 dark:hover:bg-gray-800"
                                                        onClick={() => addToQueue(person.id)}
                                                    >
                                                        <PlusIcon className="h-3.5 w-3.5"/>
                                                        Queue
                                                    </button>
                                                }
                                                <button
                                                    disabled={isSwitchingGirls}
                                                    className="inline-flex items-center rounded-full bg-pink-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-pink-500 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                                                    onClick={() => setOnStage(person.id)}
                                                >
                                                    Set On Stage
                                                </button>
                                            </div>}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                        </>}
                    </div>
                </div>
            </div>

            {venueId &&
                <SockJsClient url={baseUrl + '/ws'}
                              topics={[vtDancerMessageURI + venueId, vtDancerMessageURI + venueId + '/dj']}
                              onMessage={onMessageReceive}
                              debug={false}/>
            }

            <Dialog open={open} onClose={setOpen} className="relative z-10">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
                />

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            transition
                            className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pt-5 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-sm sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95"
                        >
                            <div>
                                <div
                                    className="mx-auto flex size-12 items-center justify-center rounded-full bg-red-500">
                                    <XMarkIcon aria-hidden="true" className="size-6 text-black"/>
                                </div>
                                <div className="mt-3 text-center sm:mt-5">
                                    <DialogTitle as="h3" className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                        Dancer Selection Failed
                                    </DialogTitle>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            Dancer has already checked out.
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            Choose a different dancer.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 sm:mt-6">
                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    className="inline-flex w-full justify-center rounded-md bg-pink-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-pink-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600"
                                >
                                    Go back to Dancer Selection
                                </button>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
        </>
    )
}
